import { loadLanguage, loadRegion } from 'state/intl/hook';
import * as DatadogLogger from 'utils/datadog';

import { ApiKeys, Service, brand, env, isLocalDev, platform } from '../environment';

import identifyUserOnInitialization from './identify-user-on-initialization';
import { getLogRocketImports } from './logrocket-imports';

export const LOGROCKET_KEY: string = ApiKeys.get(Service.logRocket);

export const SESSION_CAPTURE_TIMEOUT = 5000;

const privateFieldNames = [
  'email',
  'address',
  'name',
  'phoneNumber',
  'billingCity',
  'billingState',
  'billingZip',
  'billingApt',
  'billingAddress',
  'credit',
  'dateOfBirth'
];

export default class LogRocketHelper {
  private static instance: LogRocketHelper;

  private _logrocket: typeof import('logrocket') | null = null;
  private _runOnInit?: Function;

  private constructor() {}

  get runOnInit() {
    return this._runOnInit;
  }

  set runOnInit(runOnInit) {
    this._runOnInit = runOnInit;
  }

  get logrocket() {
    return this._logrocket;
  }

  set logrocket(lrInstance) {
    this._logrocket = lrInstance;
  }

  public static getInstance(): LogRocketHelper {
    if (!LogRocketHelper.instance) {
      LogRocketHelper.instance = new LogRocketHelper();
    }

    return LogRocketHelper.instance;
  }

  public static getSessionUrl(): Promise<string | null> {
    return new Promise(resolve => {
      const logRocketInstance = LogRocketHelper.getInstance();
      if (logRocketInstance.logrocket) {
        logRocketInstance.logrocket.getSessionURL((sessionUrl: string) => resolve(sessionUrl));
      } else {
        resolve(null);
      }
    });
  }

  public static track(trackEvent: string) {
    const logRocketInstance = LogRocketHelper.getInstance();
    if (logRocketInstance.logrocket) {
      logRocketInstance.logrocket.track(trackEvent);
    }
  }

  static async init() {
    if (isLocalDev || env() === 'test') {
      return;
    }

    const logRocketImports = getLogRocketImports();

    const { default: LogRocket } = await logRocketImports.logrocket;
    const { default: LogrocketFuzzySanitizer } = await logRocketImports.logrocketFuzzySanitizer;

    const { requestSanitizer, responseSanitizer } = LogrocketFuzzySanitizer.setup([
      ...privateFieldNames
    ]);

    const logRocketInstance = this.getInstance();

    logRocketInstance.logrocket = LogRocket;

    setTimeout(() => {
      if (logRocketInstance.logrocket) {
        logRocketInstance.logrocket.init(LOGROCKET_KEY, {
          network: {
            requestSanitizer,
            responseSanitizer
          }
        });
        LogRocket.getSessionURL((sessionUrl: string) => {
          DatadogLogger.addContext('logrocketSessionUrl', sessionUrl);
        });
        logRocketInstance._runOnInit && logRocketInstance._runOnInit();
      }
    }, SESSION_CAPTURE_TIMEOUT);

    // kiosk: this will identify the session user as the kiosk email
    // non-kiosk: this won't do anything
    identifyUserOnInitialization(logRocketInstance);

    logRocketInstance.logrocket.track(`Env: ${env()}`);
    logRocketInstance.logrocket.track(`Brand: ${brand()}`);
    logRocketInstance.logrocket.track(`Platform: ${platform()}`);
    logRocketInstance.logrocket.track(`Country: ${String(loadRegion())}`);
    logRocketInstance.logrocket.track(`Lang: ${String(loadLanguage())}`);
  }
}
